import React, { useEffect, useRef } from 'react';
import { useUserContext } from '@root/providers/user_context';
import { CircularProgress, Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoggedIn, initialized, isCheckingAuth, signIn } = useUserContext();
  const hasTriggeredRedirect = useRef(false);
  const location = useLocation();
  const currentPath = useRef(location.pathname + location.search);
  const redirectTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    currentPath.current = location.pathname + location.search;
  }, [location]);

  useEffect(() => {
    const isLoggingOut = sessionStorage.getItem('is_logging_out');
    if (isLoggingOut === 'true') {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
        redirectTimer.current = null;
      }
      hasTriggeredRedirect.current = false;
      return;
    }
    
    if (redirectTimer.current) {
      clearTimeout(redirectTimer.current);
      redirectTimer.current = null;
    }
    
    if (currentPath.current.startsWith('/auth') || currentPath.current.startsWith('/postsignout')) {
      return;
    }
    
    if (initialized && !isCheckingAuth && !isLoggedIn && !hasTriggeredRedirect.current) {
      hasTriggeredRedirect.current = true;
      
      redirectTimer.current = setTimeout(() => {
        const stillLoggingOut = sessionStorage.getItem('is_logging_out');
        if (stillLoggingOut === 'true') {
          hasTriggeredRedirect.current = false;
          return;
        }
        signIn(currentPath.current);
      }, 100);
    }

    if (isLoggedIn && hasTriggeredRedirect.current) {
      hasTriggeredRedirect.current = false;
    }
    
    return () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current);
      }
    };
  }, [initialized, isCheckingAuth, isLoggedIn, signIn]);

  if (!initialized || isCheckingAuth) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
};
